import { UserPlus, Loader2 } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  fetchReplicasActionAtom,
  replicasAtom,
  replicasLoadingAtom,
} from '@/stores';
import { ReplicaPreview } from './ReplicaPerview';

interface CreatePersonaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPersonaCreated: (replicaId?: string) => Promise<void>;
}

export function CreatePersonaDialog({
  open,
  onOpenChange,
  onPersonaCreated,
}: CreatePersonaDialogProps) {
  const fetchReplicas = useSetAtom(fetchReplicasActionAtom);
  const replicas = useAtomValue(replicasAtom);
  const isLoading = useAtomValue(replicasLoadingAtom);
  const [selectedReplicaId, setSelectedReplicaId] = useState<string>('');
  const [currentPreviewVideo, setCurrentPreviewVideo] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) {
      setSelectedReplicaId('');
      setCurrentPreviewVideo('');
      setIsCreating(false);
      return;
    }

    fetchReplicas().catch(console.error);
  }, [open, fetchReplicas]);

  // Calculate preview position
  useEffect(() => {
    if (open && dialogContentRef.current && currentPreviewVideo) {
      const rect = dialogContentRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Right of the modal window
      const previewLeft = rect.right + 20;

      // Add padding
      const maxLeft = viewportWidth - 220;
      const finalLeft = Math.min(previewLeft, maxLeft);

      // Top side of modal
      setPreviewPosition({
        top: rect.top - 12,
        left: finalLeft,
      });
    }
  }, [open, currentPreviewVideo]);

  const handleReplicaChange = (replicaId: string) => {
    setSelectedReplicaId(replicaId);
  };

  const handleVideoPreview = (videoUrl: string) => {
    setCurrentPreviewVideo(videoUrl);
  };

  const hideVideoPreview = () => {
    setCurrentPreviewVideo('');
  };

  const handleSubmit = async () => {
    const replicaIdToSend =
      // попробовать убрать дефолт
      selectedReplicaId === 'default' || selectedReplicaId === ''
        ? undefined
        : selectedReplicaId;

    setIsCreating(true);

    try {
      await onPersonaCreated(replicaIdToSend);
      setSelectedReplicaId('');
      onOpenChange(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    if (!isCreating) {
      setSelectedReplicaId('');
      onOpenChange(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleCancel}>
        <DialogContent className='sm:max-w-[500px] pt-6' ref={dialogContentRef}>
          <DialogHeader className='space-y-3'>
            <DialogTitle className='flex items-center gap-2'>
              <UserPlus className='h-5 w-5' />
              Create Interviewer Persona
            </DialogTitle>
            <DialogDescription>
              Select a Replica or use default for creating the interviewer
              persona. Point at any option to see a Replica preview.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Select Replica</label>
              <Select
                onValueChange={handleReplicaChange}
                value={selectedReplicaId}
                disabled={isLoading || isCreating}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isLoading ? 'Loading replicas...' : 'Select a replica'
                    }
                  />
                </SelectTrigger>
                <SelectContent
                  className='max-h-[300px] overflow-y-auto'
                  onMouseLeave={hideVideoPreview}
                >
                  {/* Default option */}
                  <SelectItem
                    value='default'
                    onMouseEnter={() => hideVideoPreview()}
                  >
                    Replica by default
                  </SelectItem>

                  {/* Replica list */}
                  {replicas.map(replica => (
                    <SelectItem
                      key={replica.replicaId}
                      value={replica.replicaId}
                      onMouseEnter={() =>
                        replica.thumbnailVideoUrl &&
                        handleVideoPreview(replica.thumbnailVideoUrl)
                      }
                      onMouseLeave={hideVideoPreview}
                    >
                      {replica.replicaName || 'Unknown replica'}
                    </SelectItem>
                  ))}

                  {replicas.length === 0 && !isLoading && (
                    <SelectItem value='no-replicas' disabled>
                      No Replicas available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={handleSubmit}
                disabled={isLoading || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    Creating...
                  </>
                ) : (
                  'Create Persona'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replica Preview */}
      <ReplicaPreview
        isVisible={!!currentPreviewVideo}
        previewUrl={currentPreviewVideo}
        style={previewPosition}
      />
    </>
  );
}
